import * as React from "react";
import { CheckIcon } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

interface ActionSelectItemProps {
  value: string;
  label: string;
  description: string;
  /** Colour class for the label (e.g. the action's colorVariant text class). */
  labelClassName?: string;
  disabled?: boolean;
}

/**
 * A resolution-action option that shows a coloured label plus a muted description line in the dropdown, while
 * the closed trigger stays label-only: only `Select.ItemText` is cloned into `Select.Value`, so the description
 * (rendered as a sibling of `ItemText`) never leaks into the trigger. Built on the radix primitive directly
 * because the shared `SelectItem` wraps *all* children in `ItemText`.
 */
export const ActionSelectItem: React.FC<ActionSelectItemProps> = ({
  value,
  label,
  description,
  labelClassName,
  disabled,
}) => (
  <SelectPrimitive.Item
    value={value}
    disabled={disabled}
    className={cn(
      "relative flex w-full cursor-default flex-col items-start gap-0.5 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
    )}
  >
    <span className="absolute top-2 right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>
      <span className={cn(labelClassName)}>{label}</span>
    </SelectPrimitive.ItemText>
    <span className="text-xs text-muted-foreground">{description}</span>
  </SelectPrimitive.Item>
);

export default ActionSelectItem;
